import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CareLinksActions } from './care-links.actions';
import { CareLinksApiService } from '@app/core/api/care-links';
import { LoggerService, ToastNotificationService } from '@app/core/infrastructure';

@Injectable()
export class CareLinksEffects {
  private readonly actions$ = inject(Actions);
  private readonly careLinksApi = inject(CareLinksApiService);
  private readonly logger = inject(LoggerService).scope('CareLinksEffects');
  private readonly toastService = inject(ToastNotificationService);
  private readonly translate = inject(TranslateService);

  loadCaregivers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareLinksActions.loadCaregivers),
      switchMap(() =>
        this.careLinksApi.getMyCaregivers().pipe(
          map(caregivers => CareLinksActions.loadCaregiversSuccess({ caregivers })),
          catchError(error => {
            this.logger.error('Load caregivers failed:', error);
            return of(CareLinksActions.loadCaregiversFailure({ error: error.message }));
          })
        )
      )
    )
  );

  inviteCaregiver$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareLinksActions.inviteCaregiver),
      switchMap(({ request }) =>
        this.careLinksApi.inviteCaregiver(request).pipe(
          map(careLink => CareLinksActions.inviteCaregiverSuccess({ careLink })),
          catchError(error => {
            this.logger.error('Invite caregiver failed:', error);
            const status = error.status || error.error?.status;
            const message = error.error?.detail || error.message || 'Failed to invite caregiver';
            return of(CareLinksActions.inviteCaregiverFailure({ error: message, status }));
          })
        )
      )
    )
  );

  inviteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareLinksActions.inviteCaregiverSuccess),
      tap(() => {
        this.toastService.showSuccess(this.translate.instant('settings.caregivers.inviteSuccess'));
      }),
      map(() => CareLinksActions.loadCaregivers())
    )
  );

  inviteFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareLinksActions.inviteCaregiverFailure),
      tap(({ error, status }) => {
        if (status === 409) {
          this.toastService.showError(this.translate.instant('settings.caregivers.inviteConflict'));
        } else if (status === 400) {
          this.toastService.showError(this.translate.instant('settings.caregivers.inviteSelf'));
        } else {
          this.toastService.showError(error);
        }
      })
    ),
    { dispatch: false }
  );

  revokeLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareLinksActions.revokeLink),
      switchMap(({ linkId }) =>
        this.careLinksApi.revokeLink(linkId).pipe(
          map(() => CareLinksActions.revokeLinkSuccess({ linkId })),
          catchError(error => {
            this.logger.error('Revoke link failed:', error);
            return of(CareLinksActions.revokeLinkFailure({ error: error.message }));
          })
        )
      )
    )
  );

  revokeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareLinksActions.revokeLinkSuccess),
      tap(() => {
        this.toastService.showSuccess(this.translate.instant('settings.caregivers.revokeSuccess'));
      }),
      map(() => CareLinksActions.loadCaregivers())
    )
  );

  resendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareLinksActions.resendInvitation),
      switchMap(({ linkId }) =>
        this.careLinksApi.resendInvitation(linkId).pipe(
          map(careLink => CareLinksActions.resendInvitationSuccess({ careLink })),
          catchError(error => {
            this.logger.error('Resend invitation failed:', error);
            return of(CareLinksActions.resendInvitationFailure({ error: error.message }));
          })
        )
      )
    )
  );

  resendSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareLinksActions.resendInvitationSuccess),
      tap(() => {
        this.toastService.showSuccess(this.translate.instant('settings.caregivers.resendSuccess'));
      }),
      map(() => CareLinksActions.loadCaregivers())
    )
  );
}
